import { toast } from 'react-toastify';

/**
 * Downloads a file from a blob response with proper filename extraction
 * @param {Object} response - The API response containing blob data and headers
 * @param {string} defaultFilename - Default filename if extraction fails
 */
export const downloadFileFromResponse = async (
  response,
  defaultFilename = 'exported_file.xlsx',
) => {
  try {
    
    const respHeaders = response.headers;
    const contentDisposition = respHeaders['content-disposition'];

    // Extract filename from content-disposition header
    let filename = defaultFilename;
    if (contentDisposition) {
      // First, try to get the extended filename (UTF-8 encoded)
      const extFilenameMatch = contentDisposition.match(
        /filename\*\s*=\s*(?:UTF-8'')([^;]+)/i
      );
      if (extFilenameMatch && extFilenameMatch[1]) {
        filename = decodeURIComponent(extFilenameMatch[1].trim());
      } else {
        // Fallback to the standard filename parameter
        const filenameMatch = contentDisposition.match(
          /filename\s*=\s*"([^"]+)"/i
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
    }

    // Create blob and trigger download
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Export endpoints are called with responseType 'blob', so a JSON error body
 * (e.g. "please run points calculation first") arrives wrapped in a Blob.
 * Unwrap it so the server's actionable message reaches the user instead of a
 * generic failure toast.
 */
const extractServerMessage = async (error) => {
  const data = error.response?.data;
  try {
    if (data instanceof Blob) {
      const body = JSON.parse(await data.text());
      return body.message || body.Message || null;
    }
    return data?.message || data?.Message || null;
  } catch {
    return null;
  }
};

/**
 * Generic export function that handles API call and file download
 * @param {Function} apiCall - The API function to call
 * @param {Array} apiArgs - Arguments to pass to the API function
 * @param {string} defaultFilename - Default filename if extraction fails
 * @param {string} successMessage - Success toast message
 * @param {string} loadingMessage - Loading toast message
 */
export const exportAndDownloadFile = async (
  apiCall,
  apiArgs = [],
  defaultFilename = 'exported_file.xlsx',
  successMessage = 'File was successfully exported',
  loadingMessage = 'Fetching data from the server...'
) => {
  try {
    toast.info(loadingMessage);
    const response = await apiCall(...apiArgs);
    await downloadFileFromResponse(response, defaultFilename);
    toast.success(successMessage);
  } catch (error) {
    console.error('Error exporting file:', error);
    const serverMessage = await extractServerMessage(error);
    toast.error(serverMessage || 'Failed to export file');
  }
};
